import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Modal, Fade, Typography, Divider, Button,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useForm, Controller } from 'react-hook-form';

import api from '../../utils/functions/api';
import { confirmAsync } from '../../utils/functions/globalDialog';
import * as actions from '../../redux/actions';
import { ExpensesTableApplove } from '../../components/Table';
import AddpayoutForm from '../../components/Forms/AddpayoutForm';
import ExpensesFilterPanel from '../../components/Card/ExpensesFilterPanel';
import { buildExpensesQuery } from '../../utils/functions/buildExpensesQuery';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: '80%', md: '60%' },
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
  maxHeight: '90vh',
  overflow: 'auto',
};

// modal ถังขยะ — กว้าง/สูงกว่า style ปุ่ม "เพิ่มข้อมูลการชำระเงิน" ด้านบน เพราะต้องใส่ตารางเต็ม
const trashStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', md: '85%' },
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 2, sm: 3 },
  maxHeight: '90vh',
  overflow: 'auto',
};

const PAGE_SIZE = 20;

// ชุด chip กรองสถานะของแท็บนี้ — เฉพาะ status ที่แท็บนี้เจอได้จริงตาม getTabMatch ฝั่ง backend (REJECT/
// REJECT_2/REJECT_3 — CANCEL ไม่อยู่ในแท็บนี้แล้ว ย้ายไปอยู่ในถังขยะเท่านั้น ดูปุ่ม "ถังขยะ" ด้านล่าง)
const STATUS_OPTIONS = [
  { key: 'ALL', label: 'ทั้งหมด', color: 'default', statuses: [] },
  { key: 'REJECTED', label: 'Rejected', color: 'error', statuses: ['REJECT', 'REJECT_2', 'REJECT_3'] },
];

// tab "ปฏิเสธ" — เฉพาะรายการที่ status เป็น REJECT/REJECT_2/REJECT_3 (ดู getTabMatch ใน
// expenses.service.js) กล่อง "ตัวกรอง" ใช้ ExpensesFilterPanel (shared component แก้ layout ทีเดียว
// มีผลทุก tab) — handler ที่ส่งให้ตารางมีแค่ handleReset/handleOpenPay (ไม่มีชุดอนุมัติ/ปฏิเสธเหมือน tab อื่น
// เพราะ status กลุ่มนี้มีแค่ปุ่ม "แก้ไข"/"RESET" ให้กดจริง ดู ExpensesTableApplove.js บล็อก status REJECT)
// รายการ CANCEL (ยกเลิก) ไม่โผล่ในแท็บนี้อีกแล้ว — อยู่ในปุ่ม "ถังขยะ" (modal แยก, backend tab="trash")
// เท่านั้น ลบถาวรอัตโนมัติถ้าไม่กู้คืนภายใน 30 วัน (ดู purgeOldCancelledExpenses ใน expenses.service.js
// + cron ใน scheduler.js) กู้คืนจากถังขยะ = กลับไปเป็น REJECT (เข้าแท็บนี้ปกติ) ไม่ใช่ PENDING
export default function ExpensesTabRejected({ onDoubleClickProject, refreshTick = 0 }) {
  const tabKey = 'rejected';
  const showDateRange = true;

  const dispatch = useDispatch();
  const me = useSelector((state) => state.me);
  const info = useSelector((state) => state.info);
  const projects = useSelector((state) => state.project);
  const budgetState = useSelector((state) => state.budget);

  const [dateBounds, setDateBounds] = useState({ min: null, max: null });
  const [filterOpen, setFilterOpen] = useState(true);
  const [maxLookbackText, setMaxLookbackText] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [expensesData, setExpensesData] = useState({ rows: [], total: 0, isLoading: false });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [projectSelect, setProjectSelect] = useState(null);
  const [budgetSelect, setBudgetSelect] = useState(null);
  const [filterVat, setFilterVat] = useState(false);
  const [filterWithholding, setFilterWithholding] = useState(false);
  // เลือกได้หลาย chip พร้อมกัน (multi-select) — array ว่าง = "ทั้งหมด" ไม่กรอง
  const [statusFilters, setStatusFilters] = useState([]);
  const toggleStatusFilter = (key) => {
    if (key === 'ALL') { setStatusFilters([]); return; }
    setStatusFilters((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };
  const [dateFilterField, setDateFilterField] = useState('date');
  const [appliedDateFilterField, setAppliedDateFilterField] = useState('date');
  const [dateRange, setDateRange] = useState(null);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  const [advSearchName, setAdvSearchName] = useState('');
  const [advSearchCode, setAdvSearchCode] = useState('');
  const [advSearchPayee, setAdvSearchPayee] = useState('');
  const [advSearchApplicant, setAdvSearchApplicant] = useState('');
  const [advSearchNetAmount, setAdvSearchNetAmount] = useState('');

  const [openPay, setOpenPay] = useState(false);
  const [exSelect, setExSelect] = useState('');
  const [datePay, setDatePay] = useState(new Date());
  const [paidType] = useState('TRANSFER');
  const [slipImage, setSlipImage] = useState([]);

  // ถังขยะ (status CANCEL) — modal แยกต่างหาก ไม่ปนกับตาราง "ปฏิเสธ" หลักด้านบน ไม่ต้องมี filter/pagination
  // ซับซ้อนเหมือนตารางหลัก เพราะปกติมีไม่กี่รายการ (ลบถาวรอัตโนมัติทุก 30 วันอยู่แล้ว)
  const [trashOpen, setTrashOpen] = useState(false);
  const [trashData, setTrashData] = useState({ rows: [], total: 0, isLoading: false });

  const { control, handleSubmit, reset, setValue } = useForm();
  const dateRangeRef = useRef(null);

  useEffect(() => {
    dispatch(actions.projectAll({ lite: true }));
  }, [dispatch]);

  // ช่วงวันที่จริงของรายการเบิกที่อยู่ใน tab นี้เท่านั้น (ไม่ใช่ทั้งระบบ) — ใช้ bound date-range picker
  useEffect(() => {
    if (!showDateRange) return;
    (async () => {
      try {
        const { data } = await api.get(`${process.env.REACT_APP_API_URL}/expenses/date-range?tab=${tabKey}&field=${dateFilterField}`);
        setDateBounds({
          min: data?.min ? new Date(data.min) : null,
          max: data?.max ? new Date(data.max) : null,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  }, [dateFilterField]);

  useEffect(() => {
    if (projectSelect?._id) {
      dispatch(actions.budgetAll({ project_id: projectSelect._id }));
    }
  }, [dispatch, projectSelect]);

  // infinite scroll — โหลดทีละหน้า (PAGE_SIZE รายการ) แล้วต่อท้าย rows เดิม แทนการกดเปลี่ยนหน้า
  const fetchPage = useCallback(async (targetPage, append, overrides = {}) => {
    const searchVal = overrides.search ?? search;
    const dateRangeVal = overrides.dateRange ?? dateRange;
    setExpensesData((prev) => ({ ...prev, isLoading: true }));
    try {
      const q = buildExpensesQuery({
        tab: tabKey,
        page: targetPage,
        size: PAGE_SIZE,
        sortBy,
        sortDir,
        search: searchVal,
        projectId: projectSelect?._id,
        budgetId: budgetSelect?._id,
        filterVat,
        filterWithholding,
        statusFilters,
        statusOptions: STATUS_OPTIONS,
        dateRange: dateRangeVal,
        dateFilterField,
        advSearch: {
          name: advSearchName,
          code: advSearchCode,
          payee: advSearchPayee,
          applicant: advSearchApplicant,
          netAmount: advSearchNetAmount,
        },
      });
      const { data } = await api.get(`${process.env.REACT_APP_API_URL}/expenses?${q.toString()}`);
      const newRows = data?.rows || [];
      setExpensesData((prev) => ({
        rows: append ? [...prev.rows, ...newRows] : newRows,
        total: data?.total || 0,
        isLoading: false,
      }));
      setPage(targetPage);
    } catch (error) {
      console.error(error);
      setExpensesData((prev) => ({ ...prev, isLoading: false }));
    }
  }, [
    search, projectSelect, budgetSelect, filterVat, filterWithholding, statusFilters,
    dateRange, dateFilterField, advSearchName, advSearchCode, advSearchPayee, advSearchApplicant,
    advSearchNetAmount, sortBy, sortDir,
  ]);

  const fetchData = useCallback(() => fetchPage(1, false), [fetchPage]);

  // ใช้ ref เก็บ fetchPage ตัวล่าสุดไว้เรียกจาก effect โดยไม่ให้ effect ต้อง re-run ทุกครั้งที่ filter เปลี่ยน
  // เพราะฟิลเตอร์พวกนี้ต้องรอกดปุ่ม "ค้นหา" เท่านั้นถึงจะ apply — ยกเว้น refreshTick/statusFilters/sortBy/sortDir
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  useEffect(() => {
    fetchPageRef.current(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick, statusFilters, sortBy, sortDir]);

  const handleSearch = () => {
    const nextSearch = searchInput;
    const nextDateRange = showDateRange
      ? (dateRangeRef.current?.getValue ? dateRangeRef.current.getValue() : dateRange)
      : dateRange;
    setSearch(nextSearch);
    setDateRange(nextDateRange);
    setAppliedDateFilterField(dateFilterField);
    fetchPage(1, false, { search: nextSearch, dateRange: nextDateRange });
  };

  const handleLoadMore = () => {
    if (expensesData.isLoading) return;
    fetchPage(page + 1, true);
  };

  const hasMore = expensesData.rows.length < expensesData.total;

  const mutate = async (id, payload) => {
    await dispatch(actions.expensesPut(id, payload));
    fetchData();
  };

  const actorPayload = () => ({
    approval_actor: me?.userData?.id,
    approval_actor_name: formatEmployeeThaiName(me?.userData),
  });

  // เฉพาะปุ่มที่ตารางใช้จริงกับ status REJECT — "แก้ไข" (openEditModal ในตัวตารางเอง) + "RESET" กลับไป
  // เป็น PENDING เข้าคิวอนุมัติใหม่
  const handleReset = (id) => mutate(id, { status: 'PENDING', ...actorPayload() });

  // ถังขยะ — โหลดทุกครั้งที่เปิด modal (รายการน้อย ไม่ต้อง pagination/filter) เรียงล่าสุดที่ถูกยกเลิกก่อน
  const fetchTrash = useCallback(async () => {
    setTrashData((prev) => ({ ...prev, isLoading: true }));
    try {
      const q = buildExpensesQuery({
        tab: 'trash', page: 1, size: 999999, sortBy: 'updatedAt', sortDir: 'desc',
      });
      const { data } = await api.get(`${process.env.REACT_APP_API_URL}/expenses?${q.toString()}`);
      setTrashData({ rows: data?.rows || [], total: data?.total || 0, isLoading: false });
    } catch (error) {
      console.error(error);
      setTrashData((prev) => ({ ...prev, isLoading: false }));
    }
  }, []);

  const handleOpenTrash = () => {
    setTrashOpen(true);
    fetchTrash();
  };
  const handleCloseTrash = () => setTrashOpen(false);

  // กู้คืนจากถังขยะ = CANCEL → REJECT (เข้าแท็บ "ปฏิเสธ" ปกติ) ต่างจาก handleReset (REJECT/HOLD → PENDING
  // เข้าคิวอนุมัติใหม่ทันที) — รีเฟรชทั้งถังขยะและตารางหลัก เพราะรายการย้ายข้ามแท็บกันจริง
  const handleRestoreFromTrash = async (id) => {
    await dispatch(actions.expensesPut(id, { status: 'REJECT', ...actorPayload() }));
    fetchTrash();
    fetchData();
  };

  const handleOpenPay = (e) => {
    setOpenPay(true);
    setExSelect(e);
    const net =
      e?.net_price != null
        ? Number(e.net_price)
        : (Number(e?.price) || 0) - (Number(e?.withholding_tax) || 0);
    setValue('price', net > 0 ? net : '');
  };
  const handleClosePay = () => setOpenPay(false);

  const onSubmitPay = async (data) => {
    const confirm = await confirmAsync('บันทึกข้อมูล');
    if (!confirm) return;
    const dataSubmit = {
      ...data,
      paidType,
      date: datePay,
      employee: me?.userData?._id,
      expenses: exSelect?.id,
      budget: exSelect?.budget?._id,
      slipImage,
    };
    await dispatch(actions.payoutCreate(dataSubmit));
    await dispatch(actions.payoutAll({}));
    await dispatch(actions.expensesPut(exSelect?._id, { status: 'SUCCESS' }));
    fetchData();
    reset();
    setSlipImage([]);
    handleClosePay();
  };

  const handleClearAdvanced = () => {
    setAdvSearchName('');
    setAdvSearchCode('');
    setAdvSearchPayee('');
    setAdvSearchApplicant('');
    setAdvSearchNetAmount('');
    setProjectSelect(null);
    setBudgetSelect(null);
    setFilterVat(false);
    setFilterWithholding(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          variant="outlined"
          size="small"
          color="secondary"
          startIcon={<DeleteOutlineIcon />}
          onClick={handleOpenTrash}
        >
          ถังขยะ
        </Button>
      </Box>

      <ExpensesFilterPanel
        filterOpen={filterOpen}
        onToggleFilterOpen={() => setFilterOpen((v) => !v)}
        maxLookbackText={maxLookbackText}
        showDateRange={showDateRange}
        dateRangeRef={dateRangeRef}
        dateRange={dateRange}
        dateBounds={dateBounds}
        dateFilterField={dateFilterField}
        onDateFieldChange={setDateFilterField}
        onMaxLookbackChange={setMaxLookbackText}
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        onSearch={handleSearch}
        onClearSearch={() => { setSearchInput(''); setSearch(''); fetchPage(1, false, { search: '' }); }}
        advancedOpen={advancedOpen}
        onToggleAdvanced={() => setAdvancedOpen((v) => !v)}
        projects={projects}
        projectSelect={projectSelect}
        onProjectChange={(val) => { setProjectSelect(val); setBudgetSelect(null); }}
        budgets={budgetState}
        budgetSelect={budgetSelect}
        onBudgetChange={setBudgetSelect}
        filterVat={filterVat}
        onFilterVatChange={setFilterVat}
        filterWithholding={filterWithholding}
        onFilterWithholdingChange={setFilterWithholding}
        advSearchName={advSearchName}
        onAdvSearchNameChange={setAdvSearchName}
        advSearchCode={advSearchCode}
        onAdvSearchCodeChange={setAdvSearchCode}
        advSearchPayee={advSearchPayee}
        onAdvSearchPayeeChange={setAdvSearchPayee}
        advSearchApplicant={advSearchApplicant}
        onAdvSearchApplicantChange={setAdvSearchApplicant}
        advSearchNetAmount={advSearchNetAmount}
        onAdvSearchNetAmountChange={setAdvSearchNetAmount}
        onClearAdvanced={handleClearAdvanced}
        appliedDateFilterField={appliedDateFilterField}
        appliedDateRange={dateRange}
      />

      <ExpensesTableApplove
        expenses={expensesData}
        paginationMode="infinite"
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
        highlightQuery={search}
        filter={expensesData.rows}
        statusFilters={statusFilters}
        onStatusFilterToggle={toggleStatusFilter}
        statusOptions={STATUS_OPTIONS}
        handleReset={handleReset}
        handleOpenPay={handleOpenPay}
        onDetailClose={fetchData}
        onDoubleClickProject={onDoubleClickProject}
        me={me}
        info={info}
        tabVariant="rejected"
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={(nextSortBy, nextSortDir) => { setSortBy(nextSortBy); setSortDir(nextSortDir); }}
      />

      <Modal open={openPay} onClose={handleClosePay} closeAfterTransition>
        <Fade in={openPay}>
          <Box sx={style}>
            <Typography variant="h6" component="h2" className="mb-4">
              เพิ่มข้อมูลการชำระเงิน
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <form onSubmit={handleSubmit(onSubmitPay)}>
              <AddpayoutForm
                control={control}
                Controller={Controller}
                date={datePay}
                setDate={setDatePay}
                paidType={paidType}
                expenseData={exSelect}
                slipImage={slipImage}
                setSlipImage={setSlipImage}
              />
            </form>
          </Box>
        </Fade>
      </Modal>

      <Modal open={trashOpen} onClose={handleCloseTrash} closeAfterTransition>
        <Fade in={trashOpen}>
          <Box sx={trashStyle}>
            <Typography variant="h6" component="h2" className="mb-4">
              ถังขยะ — รายการที่ยกเลิก (ลบถาวรอัตโนมัติถ้าไม่กู้คืนภายใน 30 วัน)
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <ExpensesTableApplove
              expenses={trashData}
              paginationMode="infinite"
              onLoadMore={() => {}}
              hasMore={false}
              filter={trashData.rows}
              statusOptions={[]}
              handleRestore={handleRestoreFromTrash}
              onDetailClose={fetchTrash}
              onDoubleClickProject={onDoubleClickProject}
              me={me}
              info={info}
              tabVariant="trash"
              sortBy="updatedAt"
              sortDir="desc"
              onSortChange={() => {}}
            />
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
}
